import { motion, AnimatePresence } from "framer-motion";
import React, { useContext } from "react";

import { PrimaryNavWithOutSubNav, PrimaryNavWithSubNav } from ".";
import { NavContext } from "../../index";
import styles from "../../styles/sideNav.module.scss";
import * as Types from "../../types";
import {
  animationCollapsed,
  animationExpanded,
  animationCollapsedMobile,
  animationExpandedMobile,
  animationExpandCollapsed,
  animationCollapsedTablet,
  useIsSmall,
  useIsMaxMedium,
} from "../../utils";
import { SecondaryNav } from "../secondaryNav";

interface PrimaryNavProps {
  primaryMenu: Types.Item[];
  setIsMainMenuCollapsed: (value: boolean) => void;
}

const PrimaryNav: React.FC<PrimaryNavProps> = ({ primaryMenu, setIsMainMenuCollapsed }) => {
  const navigationVal = useContext(NavContext);
  const { openSubMenu, showSubMenu, setShowSubMenu, setNavItems, navItems } = navigationVal;
  const [animationVal, setAnimationVal] = React.useState(false);

  const isSmall = useIsSmall();
  const isIntermediate = useIsMaxMedium();
  const animationVariants = {
    expanded: "EXPAND",
    collapsed: "COLLAPSE",
    expandedCollapse: "ALREADYEXPANDCOLLAPSE",
  };

  const openPrimarySubMenu = React.useCallback(
    (menu: Types.Item, evt: Event) => {
      setNavItems({
        ...navItems,
        isOpenPrimarySubmenu: menu.id,
      });
      evt.stopPropagation();

      const previousStateMatch = navItems?.isOpenPrimarySubmenu === menu.id;

      if (showSubMenu && isSmall && previousStateMatch) {
        setShowSubMenu(false);
      } else if (menu.subMenu) {
        setShowSubMenu(true);
      } else {
        setShowSubMenu(false);
      }

      openSubMenu(menu, evt);
    },
    [navItems, setNavItems, showSubMenu],
  );

  React.useEffect(() => {
    if (showSubMenu) {
      setAnimationVal(true);
    } else {
      setAnimationVal(false);
    }
  }, [showSubMenu]);

  // Motion animation variant control
  const animationVariant = isSmall
    ? {
        [animationVariants.expanded]: animationExpanded,
        [animationVariants.collapsed]: isSmall && isIntermediate ? animationCollapsedTablet : animationCollapsed,
        [animationVariants.expandedCollapse]: animationExpandCollapsed,
      }
    : {
        [animationVariants.expanded]: animationExpandedMobile,
        [animationVariants.collapsed]: animationCollapsedMobile,
      };

  return (
    <>
      {primaryMenu.map((menu: Types.Item) => (
        <li
          key={menu.id}
          data-testid={menu.id}
          id={`${menu.id}_primary_nav_element`}
          className={`${navItems?.isOpenPrimarySubmenu === menu.id ? `${styles.active}` : ""} ${
            menu.hideOnLargerScreens ? `${styles.hideLargerScreen}` : ""
          } ${menu.navDivider ? `${styles.divider}` : ""} `}
        >
          {!menu.subMenu ? (
            <PrimaryNavWithOutSubNav menu={menu} openPrimarySubMenu={openPrimarySubMenu} />
          ) : (
            <>
              <PrimaryNavWithSubNav menu={menu} openPrimarySubMenu={openPrimarySubMenu} />
              <AnimatePresence>
                {navItems?.isOpenPrimarySubmenu === menu.id && showSubMenu && (
                  <motion.div
                    initial={animationVal ? animationVariants.expandedCollapse : animationVariants.collapsed}
                    exit={animationVal ? animationVariants.collapsed : animationVariants.expandedCollapse}
                    animate={
                      menu.subMenu && navItems?.isOpenPrimarySubmenu === menu.id && showSubMenu
                        ? animationVariants.expanded
                        : animationVariants.expandedCollapse
                    }
                    variants={animationVariant}
                  >
                    <SecondaryNav secondaryMenu={menu.subMenu} setIsMainMenuCollapsed={setIsMainMenuCollapsed} />
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </li>
      ))}
    </>
  );
};

export default PrimaryNav;
